import { useQueryClient } from "@tanstack/react-query";
import {
  useApiMutation,
  useApiQuery,
  useInfiniteApiQuery,
} from "lib/react-query";
import { API_BASE_KEYS } from "services/api";
import { invalidateQueries } from "utils/invalidate-queries";

const CENTRAL_OPTIONS = { baseUrl: API_BASE_KEYS.CENTRAL };
const BASE = "announcements";

let readerFallbackReported = false;

/**
 * Losing the id is a deliberate degradation — read state goes tenant-wide
 * instead of the request failing — but the symptom ("my unread badge cleared
 * itself") is undiagnosable from a support ticket, so report it once per
 * session. Sentry is imported lazily exactly as `instrument.js` does, keeping
 * the SDK out of the main bundle.
 */
const reportReaderFallback = () => {
  if (readerFallbackReported) return;
  readerFallbackReported = true;

  import("@sentry/react")
    .then((Sentry) =>
      Sentry.captureMessage(
        "Announcements: no stored user id, read state falls back to tenant-wide",
        "warning"
      )
    )
    .catch(() => {});
};

/**
 * Announcements live in the central database while their readers live in tenant
 * databases, and the tenant access token names only the tenant — so every
 * read-state call carries the signed-in user's id to keep unread counts and
 * "mark as read" per user rather than per company.
 *
 * Read from storage on each call rather than the module-level `user` export,
 * which is captured at import time and is still null right after login.
 */
const readerParams = () => {
  let id = null;

  try {
    const stored = localStorage.getItem("user");
    id = stored ? JSON.parse(stored)?.id : null;
  } catch {
    id = null;
  }

  if (!id) reportReaderFallback();

  return { user_identifier: id ? String(id) : undefined };
};

/** Paginated cards, appended page by page behind the design's "load more". */
export const useAnnouncementList = (category, search) =>
  useInfiniteApiQuery(BASE, {
    searchParams: {
      ...readerParams(),
      category: category || undefined,
      search: search || undefined,
    },
    perPage: 9,
    fetchOptions: CENTRAL_OPTIONS,
  });

/** Active categories with their visible counts — the filter pills. */
export const useAnnouncementCategories = () =>
  useApiQuery(`${BASE}/categories`, null, false, null, CENTRAL_OPTIONS);

/** One announcement with its sanitized body; reading it marks it as read. */
export const useAnnouncement = (id) =>
  useApiQuery(
    id ? `${BASE}/${id}` : null,
    null,
    false,
    readerParams(),
    CENTRAL_OPTIONS,
    { staleTime: 0 }
  );

/** Unread count for the top-bar badge. */
export const useAnnouncementsUnreadCount = (extraOptions = {}) =>
  useApiQuery(
    `${BASE}/unread-count`,
    null,
    false,
    readerParams(),
    CENTRAL_OPTIONS,
    extraOptions
  );

/** Mark-all-read lives here so the list page never touches the primitives. */
export const useAnnouncementMutation = (callbacks = {}) =>
  useApiMutation({ ...callbacks, fetchOptions: CENTRAL_OPTIONS });

export const markAllReadUrl = () => `${BASE}/mark-all-read`;

export const markAllReadBody = () => ({
  user_identifier: readerParams().user_identifier,
});

/**
 * Refresh everything that reflects read state: the unread badge, the category
 * counts and the card list. The shared prefix helper cannot reach the list on
 * its own — `useInfiniteApiQuery` keys its cache entries off "infinite", not
 * off the URL — so the list is matched separately here.
 */
export const useInvalidateAnnouncements = () => {
  const queryClient = useQueryClient();

  return () => {
    invalidateQueries(queryClient, BASE);
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "infinite" &&
        typeof query.queryKey[1] === "string" &&
        query.queryKey[1].startsWith(BASE),
    });
  };
};

export { BASE as ANNOUNCEMENTS_BASE };
